/**
 * The Vault API Service
 * USER-CON-001: Personal Collection Management
 *
 * Handles all vault/collection-related API calls
 */

import { fetchAPI, fetchAPIBlob, fetchAPIMultipart } from './api.js';

/**
 * Get user's vault collection with optional filters
 */
export async function getVaultItems(options = {}) {
  // The backend contract is collection.py:52-56 -- page_size, sort_by, sort_order.
  // ⛔ FastAPI SILENTLY DROPS UNKNOWN QUERY PARAMS, so `per_page` and `sort` were not
  // errors: they were ignored. Paging and sorting appeared wired and were inert. Fixing
  // the path alone would have left them that way, with the list stuck on page 1 of the
  // default sort and no error anywhere to explain it.
  const params = new URLSearchParams();
  if (options.page) params.append('page', options.page);
  if (options.per_page) params.append('page_size', options.per_page);
  if (options.is_graded !== undefined) params.append('is_graded', options.is_graded);
  if (options.grading_company) params.append('grading_company', options.grading_company);
  if (options.search) params.append('search', options.search);
  if (options.sort) {
    // `sort` was a single opaque token; the backend wants the field and the direction
    // separately. Accept "field:dir" or a bare field, defaulting to desc as the
    // backend does.
    const [sortBy, sortOrder] = String(options.sort).split(':');
    params.append('sort_by', sortBy);
    params.append('sort_order', sortOrder === 'asc' ? 'asc' : 'desc');
  }

  const query = params.toString();
  const endpoint = `/vault/items${query ? `?${query}` : ''}`;

  const response = await fetchAPI(endpoint);
  return response;
}

/**
 * Get ONE vault item by id -- GET /vault/items/{id} (collection.py:125), served as
 * CollectionItemWithIssue, i.e. the item plus issue_title/series_name/cover_image/cover_date
 * from the linked comic issue.
 *
 * ⛔ V-SCHEMA-FE: useVaultItemDetail used to fetch the LIST with `search=<itemId>` and take
 * `items[0]`. `search` matches title/publisher text, not ids, so the first hit for a numeric
 * id was whatever book had that digit string in its title -- the detail screen could DISPLAY
 * A DIFFERENT ITEM than the one tapped, with Delete wired to the id in the URL. This route
 * existed the whole time.
 *
 * A missing or foreign item is a 404 (`Collection item not found`); fetchAPI throws with
 * `err.status === 404`.
 */
export async function getVaultItem(itemId) {
  return fetchAPI(`/vault/items/${itemId}`);
}

/**
 * GET /vault/items/{id}/value-trend?days=N (collection.py:864).
 *
 * @returns {Promise<{item_id:number, days:number, trend: {start_value:number, end_value:number,
 *   change_amount:number, change_percent:number, data_points:number} | null}>}
 *   `trend` is null when fewer than two snapshots exist for the window.
 */
export async function getVaultItemValueTrend(itemId, days = 30) {
  return fetchAPI(`/vault/items/${itemId}/value-trend?days=${encodeURIComponent(days)}`);
}

/**
 * Get vault collection statistics
 */
export async function getVaultStats() {
  return fetchAPI('/vault/stats');
}

/**
 * Add a single item to the vault
 */
export async function addVaultItem(itemData) {
  return fetchAPI('/vault/items', {
    method: 'POST',
    body: JSON.stringify(itemData),
  });
}

/**
 * Update a vault item
 */
export async function updateVaultItem(itemId, updates) {
  return fetchAPI(`/vault/items/${itemId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

/**
 * Remove an item from the vault
 */
export async function removeVaultItem(itemId) {
  return fetchAPI(`/vault/items/${itemId}`, {
    method: 'DELETE',
  });
}

/**
 * Search the comic catalog for a book to identify/add.
 *
 * ⭐ BETA-COMMERCE-298 — NOW POINTS AT A ROUTE THAT EXISTS: GET /comics/issues/search.
 * It previously called `/vault/search`, which this file's own comment correctly described as
 * unbuilt ("verified against the live mounted route table: /api/vault/search does not exist, in
 * any method"). Every caller therefore 404'd, and QuickAdd swallowed it into "No comics found" —
 * a dead feature that reported itself as an empty catalog.
 *
 * ⛔ RETURNS A RESULT SET, AND THE CALLER MUST MAKE THE USER PICK. Operator tenet: any end-user
 * input runs as a SEARCH returning 1..N candidates the user CONFIRMS one of. Even a single result
 * is presented for a confirming tap, never auto-applied — "having a user make a selection, even at
 * 1 result, mitigates risk associated with misidentification then commitment to end user vaults."
 * This function deliberately returns the LIST and never a single object, so a caller cannot
 * accidentally treat "one hit" as "the answer".
 *
 * @returns {Promise<{results: Array, count: number, page: number, page_size: number, query: string}>}
 */
export async function searchComicsForVault(query, options = {}) {
  const params = new URLSearchParams({ q: query });
  if (options.series) params.append('series', options.series);
  if (options.number) params.append('number', options.number);
  if (options.publisher) params.append('publisher', options.publisher);
  if (options.page) params.append('page', options.page);
  // V-SEARCH-PAGE: the endpoint declares `page_size` (ge=1, le=50, default 20). Forwarding it is
  // what lets a caller ask for 50 and halve the number of pages a user must walk — 5,727 results
  // is 287 pages at 20 and 115 at 50. Values above 50 are a 422 at the endpoint, so the caller
  // owns staying inside the declared range; this does not silently clamp, because a clamp would
  // hide a caller bug behind a page size nobody asked for.
  if (options.pageSize) params.append('page_size', options.pageSize);

  // ⚠ `year` is NOT forwarded: /comics/issues/search has no year filter, and appending an
  // unsupported param would be silently ignored rather than honoured — a filter the user believes
  // is applied but is not. Dropped explicitly instead of passed hopefully.
  return fetchAPI(`/comics/issues/search?${params.toString()}`);
}

/**
 * Creator credits for ONE issue — the confirm leg of search -> select -> confirm.
 *
 * ⭐ PER-ISSUE BY DESIGN, AND THE CALLER MUST KEEP IT THAT WAY. The endpoint joins three credit
 * tables (issue_creators 3.5M rows, comic_story_creators 11M, plus the flat columns). Calling it
 * for a 50-row result LIST would pay that cost fifty times for forty-nine cards nobody opens.
 * Fetch it when a candidate is SELECTED, which is when credits start to matter.
 *
 * ⚠ THE SERVED SHAPE DISTINGUISHES "no credits held" FROM "not loaded". `credits` is an object
 * keyed by role, and a role with nothing is OMITTED rather than sent as an empty array — so
 * `credits: {}` means the catalogue genuinely holds none for this book, which renders differently
 * from a fetch that has not finished or has failed.
 *
 * @returns {Promise<{issue_id:number, credits:Object<string,string[]>,
 *                    roles_present:string[], minimum_satisfied:boolean}>}
 */
export async function getIssueCredits(issueId) {
  return fetchAPI(`/comics/issues/${encodeURIComponent(issueId)}/credits`);
}

/**
 * PLATFORM:F1.P9-10 Phase 1: Upload CLZ CSV file for preview
 * Returns preview with field mapping, sample rows, match statistics and — V-IMPORT — the
 * `duplicates` list the conflict resolver is driven by.
 *
 * ⛔ Goes through fetchAPIMultipart, NOT a raw fetch: the raw version carried no X-CSRF-Token and
 * no Authorization fallback, so it was rejected on every deploy shape the app actually runs in.
 * (`importCLZ`, the legacy JSON-body sibling that posted to the dead POST /vault/import/clz, is
 * deleted — it was imported by VaultPage and never invoked.)
 */
export async function uploadCLZFile(file) {
  const formData = new FormData();
  formData.append('file', file);
  return fetchAPIMultipart('/vault/import/clz/upload', formData);
}

/**
 * PLATFORM:F1.P9-10 Phase 2: Upload generic CSV file for preview
 * Returns preview with auto-detected column mapping, sample rows, match statistics and duplicates.
 */
export async function uploadCSVFile(file) {
  const formData = new FormData();
  formData.append('file', file);
  return fetchAPIMultipart('/vault/import/csv/upload', formData);
}

/**
 * PLATFORM:F1.P9-10 Phase 1 / V-IMPORT: Confirm import after preview.
 *
 * Body: `{ resolutions: {conflict_key: 'skip'|'overwrite'|'keep_both'}, default_resolution,
 * skip_duplicates? }`. The resolutions map is accumulated CLIENT-SIDE in the conflict resolver and
 * submitted ONCE here — the server never sees a per-conflict round trip. `skip_duplicates` is only
 * sent when the caller names it explicitly; the old unconditional `skip_duplicates: true` made every
 * "Overwrite"/"Keep both" choice a lie.
 */
export async function confirmImport(importId, options = {}) {
  const body = {
    resolutions: options.resolutions || {},
    default_resolution: options.default_resolution || 'skip',
  };
  if (options.skip_duplicates !== undefined) {
    body.skip_duplicates = options.skip_duplicates;
  }
  return fetchAPI(`/vault/import/${importId}/confirm`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * V-IMPORT: Cancel a pending (previewed, unconfirmed) import so the one-active-import guard
 * releases. Before this route existed the 409 promised a cancel that did not exist and a redeploy
 * mid-preview locked the user out permanently.
 */
export async function cancelImport(importId) {
  return fetchAPI(`/vault/import/${importId}/cancel`, {
    method: 'POST',
  });
}

/**
 * Get import history
 */
export async function getImportHistory(limit = 10) {
  return fetchAPI(`/vault/import/history?limit=${limit}`);
}

/**
 * Get import status (legacy collection route)
 */
export async function getImportStatus(importId) {
  return fetchAPI(`/vault/import/${importId}/status`);
}

/**
 * PLATFORM:F1.P9-10 Phase 4: Rollback an import (remove all items from it)
 */
export async function rollbackImport(importId) {
  return fetchAPI(`/vault/import/${importId}/rollback`, {
    method: 'POST',
  });
}

/**
 * PLATFORM:F1.P9-10 Phase 4: Get detailed per-row import report
 */
export async function getImportDetails(importId, options = {}) {
  const params = new URLSearchParams();
  if (options.page) params.append('page', options.page);
  if (options.page_size) params.append('page_size', options.page_size);
  const query = params.toString();
  return fetchAPI(`/vault/import/${importId}/details${query ? `?${query}` : ''}`);
}

// V-EXPORT-FE: `uploadVaultCover` (POST /vault/{id}/photo) and `getAIGrade`
// (POST /vault/{id}/ai-grade) were deleted here. Neither route is mounted by the
// backend, neither had a caller, and their only tests asserted the shape of a 404.
// Cover upload with grading is `services/autoGradeService.js` (VaultUploadWithGrading).

// =============================================================================
// PLATFORM:F1.P9-10 Phase 3: Barcode Scan API
// =============================================================================

/**
 * Scan a barcode and match to vault item
 */
export async function scanBarcode(barcode, options = {}) {
  return fetchAPI('/vault/import/barcode/scan', {
    method: 'POST',
    body: JSON.stringify({
      barcode,
      barcode_type: options.barcode_type || 'auto',
      // ⛔ DEFAULT IS FALSE, AND ONLY AN EXPLICIT `true` FLIPS IT. `!== false` made the default
      // TRUE, so any caller that forgot the option had the server COMMIT the >=90% match into the
      // vault with no human confirmation — the auto-apply the operator's tenet bans ("having a user
      // make a selection, even at 1 result, mitigates risk associated with misidentification then
      // commitment to end user vaults"). No caller in this codebase asks for true.
      auto_confirm: options.auto_confirm === true,
    }),
  });
}

/**
 * Confirm a low-confidence barcode match
 */
export async function confirmBarcodeScan(barcode, matchType, matchId) {
  return fetchAPI('/vault/import/barcode/confirm', {
    method: 'POST',
    body: JSON.stringify({
      barcode,
      match_type: matchType,
      match_id: matchId,
    }),
  });
}

/**
 * Look up a barcode without adding to vault
 */
export async function lookupBarcode(barcode) {
  return fetchAPI(`/vault/import/barcode/${encodeURIComponent(barcode)}/lookup`);
}

/**
 * V-BARCODE: Bulk barcode PREVIEW from a text file (one barcode per line).
 *
 * Returns `{import_id, total, matched, unmatched, candidates: [{barcode, matched, confidence,
 * candidates: [{comic_id|product_id, title, issue_number, publisher, cover_url, product_type}]}],
 * expires_in_s}`. NOTHING is written to the vault by this call — the user ticks rows and picks a
 * candidate, then `confirmBulkBarcodeImport` commits the selections.
 *
 * ⛔ fetchAPIMultipart, not a raw fetch: the raw version carried no CSRF header and no bearer
 * fallback (see api.js). And the old route committed every >=90% match server-side, per item, with
 * no import log and therefore no rollback.
 */
export async function bulkBarcodeImport(file) {
  const formData = new FormData();
  formData.append('file', file);
  return fetchAPIMultipart('/vault/import/barcode/bulk', formData);
}

/**
 * V-BARCODE: Commit the user's selections from a bulk barcode preview.
 *
 * @param {number} importId  the preview's import_id
 * @param {Array<{barcode: string, match_type: 'comic'|'product', match_id: number}>} selections
 * @returns CLZImportResult-shaped `{import_id, status, rows_imported, rows_matched, rows_unmatched,
 *          rows_skipped, rows_failed, errors, points_awarded}`; `rollbackImport(import_id)` undoes it.
 */
export async function confirmBulkBarcodeImport(importId, selections) {
  return fetchAPI(`/vault/import/barcode/bulk/${importId}/confirm`, {
    method: 'POST',
    body: JSON.stringify({ selections }),
  });
}

// =============================================================================
// USER-CON-001 Phase 2: Smart Sets API
// =============================================================================

/**
 * Get all smart sets for current user
 */
export async function getSmartSets() {
  return fetchAPI('/vault/smart-sets');
}

/**
 * Get items matching a smart set
 */
export async function getSmartSetItems(smartSetId, options = {}) {
  const params = new URLSearchParams();
  if (options.page) params.append('page', options.page);
  if (options.page_size) params.append('page_size', options.page_size);
  const query = params.toString();
  return fetchAPI(`/vault/smart-sets/${smartSetId}/items${query ? `?${query}` : ''}`);
}

/**
 * Create a new smart set
 */
export async function createSmartSet(data) {
  return fetchAPI('/vault/smart-sets', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update a smart set
 */
export async function updateSmartSet(smartSetId, data) {
  return fetchAPI(`/vault/smart-sets/${smartSetId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Delete a smart set
 */
export async function deleteSmartSet(smartSetId) {
  return fetchAPI(`/vault/smart-sets/${smartSetId}`, {
    method: 'DELETE',
  });
}

// =============================================================================
// USER-CON-001 Phase 2: Boxes API
// =============================================================================

/**
 * Get all boxes for current user
 */
export async function getBoxes() {
  return fetchAPI('/vault/boxes');
}

/**
 * Get a single box with its items
 */
export async function getBox(boxId) {
  return fetchAPI(`/vault/boxes/${boxId}`);
}

/**
 * Get items in a box
 */
export async function getBoxItems(boxId, options = {}) {
  const params = new URLSearchParams();
  if (options.page) params.append('page', options.page);
  if (options.page_size) params.append('page_size', options.page_size);
  const query = params.toString();
  return fetchAPI(`/vault/boxes/${boxId}/items${query ? `?${query}` : ''}`);
}

/**
 * Create a new box
 */
export async function createBox(data) {
  return fetchAPI('/vault/boxes', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update a box
 */
export async function updateBox(boxId, data) {
  return fetchAPI(`/vault/boxes/${boxId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Delete a box
 */
export async function deleteBox(boxId) {
  return fetchAPI(`/vault/boxes/${boxId}`, {
    method: 'DELETE',
  });
}

/**
 * Assign items to a box
 */
export async function assignItemsToBox(boxId, itemIds) {
  // boxes.py:348 -- POST /vault/boxes/{box_id}/items, not /assign.
  return fetchAPI(`/vault/boxes/${boxId}/items`, {
    method: 'POST',
    body: JSON.stringify({ item_ids: itemIds }),
  });
}

/**
 * Unassign items from a box
 */
export async function unassignItemsFromBox(boxId, itemIds) {
  // ⛔ THIS ONE IS A METHOD CHANGE, NOT JUST A PATH CHANGE. boxes.py:406 is
  // DELETE /vault/boxes/{box_id}/items. Correcting only the path would have sent a
  // POST to the ASSIGN handler -- silently adding the items it was asked to remove.
  return fetchAPI(`/vault/boxes/${boxId}/items`, {
    method: 'DELETE',
    body: JSON.stringify({ item_ids: itemIds }),
  });
}

/**
 * Generate QR code for a box
 */
export async function generateBoxQRCode(boxId) {
  return fetchAPI(`/vault/boxes/${boxId}/qr-code`, {
    method: 'POST',
  });
}

// =============================================================================
// USER-CON-001 Phase 2: Condition Events API
// =============================================================================

/**
 * Get condition events for a collection item
 */
export async function getConditionEvents(itemId) {
  return fetchAPI(`/vault/condition-events/item/${itemId}`);
}

/**
 * Create a condition event
 */
export async function createConditionEvent(data) {
  return fetchAPI('/vault/condition-events', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update a condition event
 */
export async function updateConditionEvent(eventId, data) {
  return fetchAPI(`/vault/condition-events/${eventId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Delete a condition event
 */
export async function deleteConditionEvent(eventId) {
  return fetchAPI(`/vault/condition-events/${eventId}`, {
    method: 'DELETE',
  });
}

/**
 * Get provenance summary for an item
 */
export async function getProvenanceSummary(itemId) {
  return fetchAPI(`/vault/condition-events/item/${itemId}/summary`);
}

// =============================================================================
// USER-CON-001 Phase 2: Sell Queue API
// =============================================================================

/**
 * Get sell queue items
 */
export async function getSellQueue(options = {}) {
  const params = new URLSearchParams();
  if (options.page) params.append('page', options.page);
  if (options.page_size) params.append('page_size', options.page_size);
  if (options.sort_by) params.append('sort_by', options.sort_by);
  if (options.sort_order) params.append('sort_order', options.sort_order);
  const query = params.toString();
  return fetchAPI(`/vault/sell-queue${query ? `?${query}` : ''}`);
}

/**
 * Add items to sell queue
 */
export async function addToSellQueue(itemIds, askingPrice = null) {
  return fetchAPI('/vault/sell-queue/add', {
    method: 'POST',
    body: JSON.stringify({ item_ids: itemIds, asking_price: askingPrice }),
  });
}

/**
 * Update sell queue item
 */
export async function updateSellQueueItem(itemId, data) {
  return fetchAPI(`/vault/sell-queue/${itemId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Remove items from sell queue
 */
export async function removeFromSellQueue(itemIds) {
  return fetchAPI('/vault/sell-queue/remove', {
    method: 'POST',
    body: JSON.stringify({ item_ids: itemIds }),
  });
}

/**
 * Get sell queue stats
 */
export async function getSellQueueStats() {
  return fetchAPI('/vault/sell-queue/stats');
}

// =============================================================================
// USER-CON-001 Phase 2: Gap Analysis API
// =============================================================================

/**
 * Get collection gaps (missing issues)
 */
export async function getGaps(options = {}) {
  const params = new URLSearchParams();
  if (options.series_id) {
    options.series_id.forEach(id => params.append('series_id', id));
  }
  if (options.include_inventory !== undefined) {
    params.append('include_inventory', options.include_inventory);
  }
  if (options.limit_per_series) {
    params.append('limit_per_series', options.limit_per_series);
  }
  const query = params.toString();
  return fetchAPI(`/vault/gaps${query ? `?${query}` : ''}`);
}

/**
 * Get hunt list (prioritized gaps)
 */
export async function getHuntList(options = {}) {
  const params = new URLSearchParams();
  if (options.series_id) {
    options.series_id.forEach(id => params.append('series_id', id));
  }
  if (options.keys_only) params.append('keys_only', 'true');
  if (options.in_inventory_only) params.append('in_inventory_only', 'true');
  if (options.limit) params.append('limit', options.limit);
  const query = params.toString();
  return fetchAPI(`/vault/gaps/hunt-list${query ? `?${query}` : ''}`);
}

// =============================================================================
// IMPL-FEATURE2-SMARTSETS-20260204: Smart Set Gaps & Computation
// =============================================================================

/**
 * GET /vault/smart-sets/{id}/gaps (smart_sets.py get_smart_set_gaps).
 *
 * VAULT-S4 V-SMART-FE-REQUESTS: the route pages with `page` / `page_size` and filters with
 * `keys_only` / `in_stock_only`. This used to send `limit` / `offset`, which the pre-S4 route
 * did not declare -- FastAPI silently drops unknown query params, so every caller got page 1 of
 * 50 whatever it asked for. `limit`/`offset` are served as one-release transition aliases on the
 * S4 route; the client sends the canonical names.
 *
 * @returns {Promise<{smart_set_id:number, smart_set_name:string,
 *   completion:{total_in_set:number, owned_count:number, completion_pct:number, gap_count:number,
 *     gaps_in_stock:number, key_count:number, estimated_gap_cost:number|null},
 *   gaps: Array<{id:number, comic_issue_id:number, issue_number:string, series_name:string|null,
 *     cover_date:string|null, cover_url:string|null, is_key_issue:boolean, key_reason:string|null,
 *     estimated_value:number|null, in_stock_at_mdm:boolean, mdm_product_id:number|null}>,
 *   total_gaps:number, page:number, page_size:number}>}
 */
export async function getSmartSetGaps(smartSetId, options = {}) {
  const params = new URLSearchParams();
  if (options.page) params.append('page', options.page);
  if (options.page_size) params.append('page_size', options.page_size);
  if (options.keys_only) params.append('keys_only', 'true');
  if (options.in_stock_only) params.append('in_stock_only', 'true');
  const query = params.toString();
  return fetchAPI(`/vault/smart-sets/${smartSetId}/gaps${query ? `?${query}` : ''}`);
}

/**
 * POST /vault/smart-sets/{id}/gaps/want -- body GapWantRequest `{comic_issue_ids}`.
 *
 * VAULT-S4 V-SMART-FE-REQUESTS: keyed on `comic_issue_id`, the key GET /gaps serves and the
 * want-list route consumes. The pre-S4 client posted `{gap_ids}` -- smart_set_gaps ROW ids,
 * which no response ever carried -- to a route that read `gap_ids` from the QUERY STRING, so
 * the body was ignored and every call was a 422. `gap_ids` survives server-side as a
 * deprecated one-release alias; the client never sends it.
 *
 * @param {number} smartSetId
 * @param {number[]} comicIssueIds
 * @returns {Promise<{message:string, want_list_id:number, added:number, already_listed:number,
 *   comic_issue_ids:number[]}>}
 */
export async function addGapsToWantList(smartSetId, comicIssueIds) {
  return fetchAPI(`/vault/smart-sets/${smartSetId}/gaps/want`, {
    method: 'POST',
    body: JSON.stringify({ comic_issue_ids: comicIssueIds }),
  });
}

/**
 * POST /vault/smart-sets/custom -- body CustomSmartSetCreate.
 *
 * VAULT-S4 V-SMART-FE-REQUESTS: the typed shape is `{name, criteria_type, criteria_value,
 * series_id?, volume?, description?}`. Only the keys CustomSmartSetCreate declares are
 * forwarded, so a caller cannot smuggle a stale field name into the body and have the server
 * drop it silently (the pre-S4 schema had no criteria_type/criteria_value at all: both were
 * discarded and every custom set persisted as `query={}` -- a match-all).
 *
 * `series_run` needs `series_id` (the server answers 422 "series_run requires series_id: search
 * the catalog and select a series" without one); `era` takes one of the four era labels;
 * `publisher` takes the publisher name; `key_issues` and `custom` take no value.
 */
const CUSTOM_SMART_SET_KEYS = Object.freeze([
  'name', 'description', 'color', 'icon',
  'criteria_type', 'criteria_value', 'series_id', 'volume',
]);

export async function createCustomSmartSet(data) {
  const body = {};
  for (const key of CUSTOM_SMART_SET_KEYS) {
    if (data[key] !== undefined) body[key] = data[key];
  }
  return fetchAPI('/vault/smart-sets/custom', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * POST /vault/smart-sets/compute -- body SmartSetComputeRequest `{set_types?, force_recompute}`.
 *
 * VAULT-S4 V-SMART-FE-REQUESTS: the pre-S4 route read `set_types` from the QUERY STRING and
 * this client posted a JSON body, so the body was ignored (and the schema that existed for it
 * was unused). The S4 route takes the body. `force_recompute=false` lets the server skip a
 * recompute when the stored sets are fresh (`skipped: true` in the response); an explicit
 * user refresh passes `force_recompute: true`.
 *
 * @returns {Promise<{sets_created:number, sets_updated:number, total_items_processed:number,
 *   gaps_identified:number, skipped:boolean}>}
 */
export async function computeSmartSets(options = {}) {
  const body = { force_recompute: Boolean(options.force_recompute) };
  if (options.set_types) body.set_types = options.set_types;
  return fetchAPI('/vault/smart-sets/compute', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// =============================================================================
// IMPL-FEATURE2-SMARTSETS-20260204: Variant Matrix & Gap Alerts
// =============================================================================

/**
 * Get variant matrix for a series/issue
 */
export async function getVariantMatrix(seriesId, issueNumber) {
  return fetchAPI(`/vault/gaps/variant-matrix/${seriesId}/${encodeURIComponent(issueNumber)}`);
}

/**
 * Get in-stock gap alerts
 */
export async function getGapAlerts(options = {}) {
  const params = new URLSearchParams();
  if (options.limit) params.append('limit', options.limit);
  const query = params.toString();
  return fetchAPI(`/vault/gaps/gap-alerts${query ? `?${query}` : ''}`);
}

/**
 * Dismiss a gap alert
 */
export async function dismissGapAlert(gapId) {
  return fetchAPI(`/vault/gaps/gap-alerts/${gapId}/dismiss`, {
    method: 'PUT',
  });
}

// =============================================================================
// USER-CON-001 OPT-005: Collection Export API
// =============================================================================

/**
 * The extension an export format downloads as; the server names json `.json` and
 * everything else (csv, ebay, clz) `.csv`.
 */
export function exportFileExtension(format) {
  return format === 'json' ? 'json' : 'csv';
}

/**
 * Export collection items -- POST /vault/export (collection_export.py:420-445).
 *
 * ⛔ V-EXPORT-FE: the server answers `text/csv` for csv/ebay/clz and `application/json`
 * for json, with `Content-Disposition: attachment; filename=...`. This went through
 * fetchAPI, which calls `response.json()` unconditionally, so every non-JSON export was a
 * parse error and the callers' "trigger a download from the blob" branches were dead code
 * behind a `typeof result === 'string' || result instanceof Blob` guard that could never
 * be true. Now a real file fetch.
 *
 * @returns {Promise<{blob: Blob, filename: string}>} the file body and the server's
 *   filename (falling back to `vault_export.<ext>` when the header is absent).
 */
export async function exportCollection(options = {}) {
  const format = options.format || 'csv';
  const body = {
    format,
    item_ids: options.item_ids || null,
    include_values: options.include_values !== false,
    include_grades: options.include_grades !== false,
    include_notes: options.include_notes || false,
  };

  // Phase 2: Filter parameters
  if (options.grade_min != null) body.grade_min = options.grade_min;
  if (options.grade_max != null) body.grade_max = options.grade_max;
  if (options.date_from) body.date_from = options.date_from;
  if (options.date_to) body.date_to = options.date_to;
  if (options.publisher) body.publisher = options.publisher;

  return fetchAPIBlob('/vault/export', {
    method: 'POST',
    body: JSON.stringify(body),
    fallbackFilename: `vault_export.${exportFileExtension(format)}`,
  });
}

/**
 * Export sell queue for eBay -- GET /vault/export/sell-queue (collection_export.py:448).
 * Always text/csv.
 *
 * @returns {Promise<{blob: Blob, filename: string}>}
 */
export async function exportSellQueue(format = 'ebay') {
  return fetchAPIBlob(`/vault/export/sell-queue?format=${encodeURIComponent(format)}`, {
    fallbackFilename: 'sell_queue.csv',
  });
}

// =============================================================================
// MDM-UNIFIED-001 Phase 7: Signature Tracking API
// =============================================================================

/**
 * Get all signatures for the current user's collection
 */
export async function getSignatures(options = {}) {
  const params = new URLSearchParams();
  if (options.collection_item_id) params.append('collection_item_id', options.collection_item_id);
  if (options.creator_id) params.append('creator_id', options.creator_id);
  if (options.is_witnessed !== undefined) params.append('is_witnessed', options.is_witnessed);
  if (options.page) params.append('page', options.page);
  if (options.per_page) params.append('per_page', options.per_page);
  const query = params.toString();
  return fetchAPI(`/vault/signatures${query ? `?${query}` : ''}`);
}

/**
 * Get a single signature by ID
 */
export async function getSignature(signatureId) {
  return fetchAPI(`/vault/signatures/${signatureId}`);
}

/**
 * Create a new signature on a collection item
 */
export async function createSignature(data) {
  return fetchAPI('/vault/signatures', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update a signature
 */
export async function updateSignature(signatureId, data) {
  return fetchAPI(`/vault/signatures/${signatureId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Delete a signature
 */
export async function deleteSignature(signatureId) {
  return fetchAPI(`/vault/signatures/${signatureId}`, {
    method: 'DELETE',
  });
}

// =============================================================================
// MDM-UNIFIED-001 Phase 8: Signature Premium Index API
// =============================================================================

/**
 * Get premium estimate for a signature
 */
export async function getSignaturePremiumEstimate(signerName, options = {}) {
  const params = new URLSearchParams({ signer_name: signerName });
  if (options.creator_id) params.append('creator_id', options.creator_id);
  if (options.witness_type) params.append('witness_type', options.witness_type);
  return fetchAPI(`/vault/signature-premiums/estimate?${params.toString()}`);
}

/**
 * Get top premium creators
 */
export async function getTopSignaturePremiums(options = {}) {
  const params = new URLSearchParams();
  if (options.witness_category) params.append('witness_category', options.witness_category);
  if (options.high_impact_only) params.append('high_impact_only', 'true');
  if (options.limit) params.append('limit', options.limit);
  const query = params.toString();
  return fetchAPI(`/vault/signature-premiums/top${query ? `?${query}` : ''}`);
}

/**
 * Search signature premiums by name
 */
export async function searchSignaturePremiums(query, limit = 20) {
  const params = new URLSearchParams({ q: query, limit: limit });
  return fetchAPI(`/vault/signature-premiums/search?${params.toString()}`);
}

/**
 * V-3 (D60): per-item auto-grade history.
 *
 * AutoGradeHistoryPanel is a PER-ITEM timeline, but the only history endpoint was user-scoped
 * and paginated -- page 1 of a user's whole history may contain none of the item being viewed.
 * VaultDetailPane worked around that by passing `item.auto_grade_history`, a field nothing has
 * ever served, so the panel received [] forever and rendered "No grading history" over real
 * events. `vault_item_id` is an optional filter added to the existing endpoint; omitting it
 * still returns the user-wide history unchanged.
 */
export async function getAutoGradeHistory(vaultItemId, { page = 1, perPage = 20 } = {}) {
  const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
  if (vaultItemId != null) params.set('vault_item_id', String(vaultItemId));
  return fetchAPI(`/vault/auto-grade/history?${params.toString()}`);
}
